/**
 * ComparisonChart Component - Compare two periods side by side
 */
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import type { SalesDataPoint, DateRange } from './types';

interface ComparisonChartProps {
  period1Data: SalesDataPoint[];
  period2Data: SalesDataPoint[];
  period1Label: string;
  period2Label: string;
  metric: 'revenue' | 'shares';
}

export const ComparisonChart: React.FC<ComparisonChartProps> = ({
  period1Data, period2Data, period1Label, period2Label, metric
}) => {
  // Normalize data for comparison (align by day of period)
  const maxLength = Math.max(period1Data.length, period2Data.length);
  const comparisonData = [];

  for (let i = 0; i < maxLength; i++) {
    const p1 = period1Data[i];
    const p2 = period2Data[i];
    comparisonData.push({
      label: `Day ${i + 1}`,
      [period1Label]: p1 ? (metric === 'revenue' ? p1.revenue : p1.shares) : 0,
      [period2Label]: p2 ? (metric === 'revenue' ? p2.revenue : p2.shares) : 0,
    });
  }

  // Calculate totals
  const total1 = period1Data.reduce((s, d) => s + (metric === 'revenue' ? d.revenue : d.shares), 0);
  const total2 = period2Data.reduce((s, d) => s + (metric === 'revenue' ? d.revenue : d.shares), 0);
  const diff = total1 - total2;
  const diffPercent = total2 > 0 ? ((total1 - total2) / total2) * 100 : 0;

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold">📊 Period Comparison</h3>
        <div className="text-right">
          <p className={`text-sm font-medium ${diff >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {diff >= 0 ? '+' : ''}{metric === 'revenue' ? `$${diff.toLocaleString()}` : diff.toLocaleString()}
            <span className="text-sm ml-1">({diffPercent >= 0 ? '+' : ''}{diffPercent.toFixed(1)}%)</span>
          </p>
        </div>
      </div>

      {/* Summary boxes */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-blue-900/30 border border-blue-700/50 rounded-lg p-3">
          <p className="text-blue-400 text-sm">{period1Label}</p>
          <p className="text-white font-bold text-lg">
            {metric === 'revenue' ? `$${total1.toLocaleString()}` : total1.toLocaleString()}
          </p>
          <p className="text-gray-500 text-sm">{period1Data.length} data points</p>
        </div>
        <div className="bg-purple-900/30 border border-purple-700/50 rounded-lg p-3">
          <p className="text-purple-400 text-sm">{period2Label}</p>
          <p className="text-white font-bold text-lg">
            {metric === 'revenue' ? `$${total2.toLocaleString()}` : total2.toLocaleString()}
          </p>
          <p className="text-gray-500 text-sm">{period2Data.length} data points</p>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={comparisonData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey="label" stroke="#9ca3af" fontSize={10} />
          <YAxis stroke="#9ca3af" fontSize={10} />
          <Tooltip
            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
            labelStyle={{ color: '#fff' }}
          />
          <Legend />
          <Bar dataKey={period1Label} fill="#3b82f6" radius={[2, 2, 0, 0]} />
          <Bar dataKey={period2Label} fill="#a855f7" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

interface ComparisonModeProps {
  enabled: boolean;
  onToggle: () => void;
  period1: DateRange | null;
  period2: DateRange | null;
  onPeriod1Change: (range: DateRange) => void;
  onPeriod2Change: (range: DateRange) => void;
  monthOptions: DateRange[];
}

export const ComparisonModeToggle: React.FC<ComparisonModeProps> = ({
  enabled, onToggle, period1, period2, onPeriod1Change, onPeriod2Change, monthOptions
}) => {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
          enabled ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
        }`}
      >
        📊 Compare Periods
      </button>

      {enabled && (
        <>
          <select
            value={period1?.value || ''}
            onChange={(e) => {
              const found = monthOptions.find(m => m.value === e.target.value);
              if (found) onPeriod1Change(found);
            }}
            className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
          >
            <option value="">Period 1</option>
            {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <span className="text-gray-500">vs</span>
          <select
            value={period2?.value || ''}
            onChange={(e) => {
              const found = monthOptions.find(m => m.value === e.target.value);
              if (found) onPeriod2Change(found);
            }}
            className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
          >
            <option value="">Period 2</option>
            {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </>
      )}
    </div>
  );
};

export default ComparisonChart;

